import {
    type ContextType,
    createContext,
    type FC,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { type Navigator as BaseNavigator, UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";
import {
    ActionButton,
    CancelButton,
    Dialog,
    DialogType,
    noop,
} from "@onshopfront/components";
import { type Blocker, type History, type Transition } from "history";

// Code largely copied from https://codesandbox.io/s/stupefied-heyrovsky-erqufz
// because Prompt was removed in React Router v6:
// https://github.com/remix-run/react-router/blob/main/docs/upgrading/v5.md#prompt-is-not-currently-supported

interface Navigator extends BaseNavigator {
    block: History["block"];
}

type NavigationContextWithBlock = ContextType<typeof NavigationContext> & {
    navigator: Navigator;
};

export const NavigationBlockContext = createContext<(message: ReactNode) => (void | Promise<void>)>(
    () => Promise.resolve()
);

export const NavigationBlock: FC<{
    children: ReactNode;
}> = ({ children }) => {
    const rej = useRef<null | (() => void)>(null);
    const res = useRef<null | (() => void)>(null);
    const [ visible, setVisible ] = useState(false);
    const [ message, setMessage ] = useState<ReactNode>("");

    const showPrompt = useCallback((message: ReactNode) => {
        return new Promise<void>((resolve, reject) => {
            rej.current = reject;
            res.current = resolve;
            setVisible(true);
            setMessage(message);
        });
    }, []);

    const close = useCallback(() => {
        if(rej.current) {
            rej.current();
        }

        setVisible(false);
    }, []);

    const confirm = useCallback(() => {
        if(res.current) {
            res.current();
        }

        setVisible(false);
    }, []);

    return (
        <NavigationBlockContext.Provider value={showPrompt}>
            {children}
            {visible && (
                <div className="relative z-1">
                    <Dialog
                        id=""
                        remove={noop}
                        type={DialogType.Danger}
                        title="Confirm Leaving"
                        content={message}
                        blockClosing
                        buttons={(
                            <>
                                <CancelButton
                                    onClick={close}
                                />
                                <ActionButton
                                    onClick={confirm}
                                >
                                    Leave
                                </ActionButton>
                            </>
                        )}
                    />
                </div>
            )}
        </NavigationBlockContext.Provider>
    );
};

export const useBlocker = (blocker: Blocker, when: boolean | (() => boolean) = true): void => {
    const { navigator } = useContext(NavigationContext) as NavigationContextWithBlock;
    const currentBlocker = useRef<null | (() => void)>(null);

    useEffect(() => {
        if(typeof when === "boolean" && !when || typeof navigator.block === "undefined") {
            return;
        }

        currentBlocker.current = navigator.block((transition: Transition) => {
            const autoUnblock = {
                ...transition,
                retry() {
                    currentBlocker.current?.();
                    transition.retry();
                },
            };

            if(typeof when === "function" && !when()) {
                autoUnblock.retry();

                return;
            }

            blocker(autoUnblock);
        });

        return () => {
            // Clears the current blocker whenever the values have changed or on unmount of this hook
            if(currentBlocker.current) {
                currentBlocker.current();
                currentBlocker.current = null;
            }
        };
    }, [ navigator, blocker, when ]);
};

export const usePrompt = (when: boolean | (() => boolean) = true, message: ReactNode = ""): void => {
    const showPrompt = useContext(NavigationBlockContext);
    const blocker = useCallback(async (transition: Transition) => {
        try {
            await showPrompt(message);
            transition.retry();
        } catch {
            // Do nothing
        }
    }, [ showPrompt, message ]);

    return useBlocker(blocker, when);
};

export const Prompt: FC<{
    when: boolean | (() => boolean);
    message: ReactNode;
}> = ({ when, message }) => {
    usePrompt(when, message);

    return null;
};
